import type { ReactNode } from 'react';
import { compareOptional, ResultsSort } from '../../shared/results_page/results_sort.js';
import type { HotelFilterableOffer } from './hotel_results_filters.js';

/** Os cinco critérios do protótipo (`OrdemDaLista`). `rewards` ficou de fora lá
 *  porque nada na base o sustenta, e aqui vale o mesmo. */
export type HotelResultsOrder = 'recommended' | 'price' | 'distance' | 'reviews' | 'stars';

export const HOTEL_RESULTS_ORDERS: readonly HotelResultsOrder[] = [
  'recommended',
  'price',
  'distance',
  'reviews',
  'stars',
];

export const DEFAULT_HOTEL_RESULTS_ORDER: HotelResultsOrder = 'recommended';

export type HotelResultsSortCopy = {
  sortLabel: string;
  orderLabel(order: HotelResultsOrder): string;
};

/** Pelo EIXO do selo (`icon`), não pelo `id`: o `id` é a chave da lista e quem
 *  produz pode precisar de ids únicos. */
const isRecommended = (facts: HotelFilterableOffer) =>
  facts.offer.badges.some((badge) => badge.icon === 'recommended');

/**
 * A ordenação, pura.
 *
 * `recommended` NÃO é "a ordem que veio": puxa o recomendado para o topo e o
 * `sort` estável do JS preserva a curadoria atrás dele — quem não tem selo nunca
 * sobe por engano (mesma regra de `ordenarHoteis` no protótipo).
 *
 * `stars` é a NOTA (0–5), não a classificação do prédio: é ela que a lista
 * mostra ao lado da estrela, e ordenar por um número que não está na tela faria
 * a ordem parecer aleatória.
 */
export function sortHotelOffers(
  offers: readonly HotelFilterableOffer[],
  order: HotelResultsOrder,
): HotelFilterableOffer[] {
  const sorted = [...offers];
  switch (order) {
    case 'recommended':
      return sorted.sort((a, b) => Number(isRecommended(b)) - Number(isRecommended(a)));
    case 'price':
      return sorted.sort((a, b) => compareOptional(a.nightlyPrice, b.nightlyPrice, 'asc'));
    case 'distance':
      return sorted.sort((a, b) => compareOptional(a.distance, b.distance, 'asc'));
    case 'reviews':
      return sorted.sort((a, b) => compareOptional(a.reviewCount, b.reviewCount, 'desc'));
    case 'stars':
      return sorted.sort((a, b) => compareOptional(a.rating, b.rating, 'desc'));
    default: {
      const exhaustive: never = order;
      return exhaustive;
    }
  }
}

/** O ordenador do hotel: os cinco critérios sobre o `ResultsSort` da casca. */
export function HotelResultsSort({
  value,
  copy,
  disabled,
  onChange,
}: {
  readonly value: HotelResultsOrder;
  readonly copy: HotelResultsSortCopy;
  readonly disabled?: boolean;
  readonly onChange: (order: HotelResultsOrder) => void;
}): ReactNode {
  return (
    <ResultsSort
      value={value}
      orders={HOTEL_RESULTS_ORDERS}
      sortLabel={copy.sortLabel}
      orderLabel={(order) => copy.orderLabel(order)}
      disabled={disabled}
      onChange={onChange}
    />
  );
}
